import { Injectable } from '@angular/core';
import { FusionContentCaptivate, FusionContentHtmlPage, FusionContentIframely, FusionContentKnowledgeWorker, FusionContentLxt, FusionContentMiroByTemplate, FusionContentMiroByUrl, FusionContentRise, FusionContentStoryline, FusionContentYoutube, SpfBucketFileFrontend, ToolConfigCustomToolKit } from '@shared/index';
import { BlocklyStorageService } from './blockly-storage.service';
import { FusionService } from '@shared-angular/services/fusion.service';
import { RouteShareService } from './route-share.service';
import { SocketAdminService } from '@shared-angular/services/socketadmin.service';
import { StageAdminService } from '@shared-angular/services/stage-admin.service';

export type EditorItemType = "global_js" | "stage_exit" | "stage_enter";
export type EditorListItem = {
  name: string,
  xml: string,
  uniqueId: string,
  code: string,
  isDirty: boolean,
  onFinishMethod: (code: string) => Promise<void>
}
export type EditorList = {
  global: EditorListItem,
  stages: {
    stage_name: string,
    entry: EditorListItem,
    exit: EditorListItem
  }[],
  fusion: EditorListItem[],
  ifthen: (EditorListItem & { skillid: string, id: string, description: string })[]
}


@Injectable({
  providedIn: 'root'
})
export class EditorItemsService {
  public selectedEditorDisplayName: string = '';

  constructor(
    private rss: RouteShareService,
    private stagesService: StageAdminService,
    private blockyStorage: BlocklyStorageService,
    private fusionService: FusionService,
    private adminSocketService: SocketAdminService
  ) {
    void this.initSocket();
  }

  private async initSocket() {
    const io = await this.adminSocketService.getIo();
    io.on("fusionConfigDelete", () => { this.loadEditorFusionItems(); });
    io.on("fusionConfigFieldChanged", () => { this.loadEditorFusionItems(); });
    io.on("fusionConfigCreated", () => { this.loadEditorFusionItems(); });
    io.on("stageCreated", () => { this.loadEditorStageItems(); });
    io.on("stageDelete", () => { this.loadEditorStageItems(); });
    io.on("stageFieldUpdate", () => { this.loadEditorStageItems(); });
    io.on("botConfigChanged", () => { this.loadEditorIfthenItems(); });
    io.on("botConfigFieldChanged", () => { this.loadEditorIfthenItems(); });
  }

  public hasDirtyItems(): boolean {
    if (this.rss.editorItems.global.isDirty) return true;
    return this.rss.editorItems.stages.some(item => item.entry.isDirty || item.exit.isDirty);
  }

  public seletItem(editorListItem: EditorListItem, displayName: string = '') {
    if (displayName === '') {
      displayName = editorListItem.name;
    }
    if (editorListItem.uniqueId.startsWith("stage")) {
      const stage = this.rss.editorItems.stages.find((stage) => {
        if (stage.entry.uniqueId === editorListItem.uniqueId) return true;
        if (stage.exit.uniqueId === editorListItem.uniqueId) return true;
        return false;
      });
      this.selectedEditorDisplayName = stage?.stage_name + " (" + editorListItem.name + ")";
    } else {
      this.selectedEditorDisplayName = displayName;
    }
    this.rss.editorItem = editorListItem;
  }

  public getEditorItemByUniqueId(uniqueId: string): EditorListItem | undefined {
    if (this.rss.editorItems.global.uniqueId === uniqueId) {
      return this.rss.editorItems.global;
    }
    for (const stage of this.rss.editorItems.stages) {
      if (stage.entry.uniqueId === uniqueId) {
        return stage.entry;
      }
      if (stage.exit.uniqueId === uniqueId) {
        return stage.exit;
      }
    }
    for (const fusion of this.rss.editorItems.fusion) {
      if (fusion.uniqueId === uniqueId) {
        return fusion;
      }
    }
    for (const ifthen of this.rss.editorItems.ifthen) {
      if (ifthen.uniqueId === uniqueId) {
        return ifthen;
      }
    }
    return undefined;
  }

  public loadEditorItems() {
    this.rss.editorItems.global =
    {
      xml: this.blockyStorage.getStageXmlByUniqueId("global_js_" + this.rss.botConfig._id),
      isDirty: false,
      code: this.rss.botConfig.customJs,
      name: "JS Global",
      uniqueId: "global_js_" + this.rss.botConfig._id,
      onFinishMethod: async (code) => {
        this.rss.botConfig.customJs = code;
        await this.rss.saveBotConfigField('customJs');
        if (code === '') {
          await this.blockyStorage.updateStage("global_js_" + this.rss.botConfig._id, this.blockyStorage.EMPTYBLOCKXML_FOR_GLOBAL, "");
        }
      }
    };
    this.loadEditorStageItems();
    this.loadEditorFusionItems();
    this.loadEditorIfthenItems();
  }

  private loadEditorStageItems() {
    this.rss.editorItems.stages = [];
    this.stagesService.stages.forEach(stage => {
      this.rss.editorItems.stages.push({
        stage_name: stage.title,
        entry: {
          xml: this.blockyStorage.getStageXmlByUniqueId("stage_enter_" + stage._id),
          isDirty: false,
          code: stage.enterJS,
          name: "Betreten",
          uniqueId: "stage_enter_" + stage._id,
          onFinishMethod: async (code: string) => {
            this.rss.botConfig.customJs = code;
            const s = this.stagesService.getStageById(stage._id);
            if (s) { s.enterJS = code; }
            await this.stagesService.updateStagesField(stage._id, 'enterJS', code);
            if (code === '') {
              await this.blockyStorage.updateStage("stage_enter_" + this.rss.botConfig._id, this.blockyStorage.EMPTYBLOCKXML, "");
            }
          }
        },
        exit: {
          xml: this.blockyStorage.getStageXmlByUniqueId("stage_exit_" + stage._id),
          isDirty: false,
          code: stage.exitJS,
          name: "Verlassen",
          uniqueId: "stage_exit_" + stage._id,
          onFinishMethod: async (code: string) => {
            this.rss.botConfig.customJs = code;
            const s = this.stagesService.getStageById(stage._id);
            if (s) { s.exitJS = code; }
            await this.stagesService.updateStagesField(stage._id, 'exitJS', code);
            if (code === '') {
              await this.blockyStorage.updateStage("stage_exit_" + this.rss.botConfig._id, this.blockyStorage.EMPTYBLOCKXML, "");
            }
          }
        }
      });
    });
  }

  private loadEditorFusionItems() {
    this.rss.editorItems.fusion = [];
    this.fusionService.fusionContent.forEach(fusion => {
      let fusionCode: string | undefined;
      if (fusion.metadata.type === 'htmlpage') fusionCode = (fusion as SpfBucketFileFrontend<string, FusionContentHtmlPage>).metadata.customJS;
      if (fusion.metadata.type === 'rise') fusionCode = (fusion as SpfBucketFileFrontend<string, FusionContentRise>).metadata.customJS;
      if (fusion.metadata.type === 'storyline') fusionCode = (fusion as SpfBucketFileFrontend<string, FusionContentStoryline>).metadata.customJS;
      if (fusion.metadata.type === 'captivate') fusionCode = (fusion as SpfBucketFileFrontend<string, FusionContentCaptivate>).metadata.customJS;
      if (fusion.metadata.type === 'lxt') fusionCode = (fusion as SpfBucketFileFrontend<string, FusionContentLxt>).metadata.customJS;
      if (fusion.metadata.type === 'miro-by-template') fusionCode = (fusion as SpfBucketFileFrontend<string, FusionContentMiroByTemplate>).metadata.customJS;
      if (fusion.metadata.type === 'miro-by-url') fusionCode = (fusion as SpfBucketFileFrontend<string, FusionContentMiroByUrl>).metadata.customJS;
      if (fusion.metadata.type === 'knowledgeworker') fusionCode = (fusion as SpfBucketFileFrontend<string, FusionContentKnowledgeWorker>).metadata.customJS;
      if (fusion.metadata.type === 'youtube') fusionCode = (fusion as SpfBucketFileFrontend<string, FusionContentYoutube>).metadata.customJS;
      if (fusion.metadata.type.startsWith('iframely')) fusionCode = (fusion as SpfBucketFileFrontend<string, FusionContentIframely>).metadata.customJS;
      if (fusionCode !== undefined) {
        this.rss.editorItems.fusion.push({
          xml: this.blockyStorage.getStageXmlByUniqueId("fusion_" + fusion._id),
          isDirty: false,
          code: fusionCode,
          name: fusion.metadata.name,
          uniqueId: "fusion_" + fusion._id,
          onFinishMethod: async (code: string) => {
            this.fusionService.selectedFusionContent = this.fusionService.fusionContent.find(fc => fc._id == fusion._id);
            await this.fusionService.updateFusionField('customJS', code);
          }
        });
      }
    });
  }

  private loadEditorIfthenItems() {
    this.rss.editorItems.ifthen = [];
    this.rss.botConfig.toolConfigs.forEach(tc => {
      if (tc.tool === 'customtoolkit') {
        this.rss.editorItems.ifthen.push({
          xml: this.blockyStorage.getStageXmlByUniqueId("ifthen_" + tc.id),
          isDirty: false,
          code: (tc as ToolConfigCustomToolKit).content,
          name: (tc as ToolConfigCustomToolKit).name,
          skillid: (tc as ToolConfigCustomToolKit).skillId,
          id: (tc as ToolConfigCustomToolKit).id,
          description: (tc as ToolConfigCustomToolKit).description,
          uniqueId: "ifthen_" + tc.id,
          onFinishMethod: async (code: string) => {
            const selectedIfthen = this.rss.botConfig.toolConfigs.find(tool => tool.id == tc.id) as ToolConfigCustomToolKit;
            selectedIfthen.content = code;
            await this.rss.saveBotConfigField('toolConfigs');
          }
        });
      }
    });
  }

  public blocklyIsPossible() {
    return this.blocklyIsPossibleFor(this.rss.editorItem);
  }

  public blocklyIsPossibleFor(editorItem: EditorListItem | undefined) {
    if (!editorItem) return false;
    const storexXml = this.blockyStorage.getStageXmlByUniqueId(editorItem.uniqueId);
    if (editorItem.code.trim() !== "" && this.isEmptyXML(storexXml)) {
      return false;
    }
    return true;
  }

  public isEmptyXML(xml: string) {
    return xml === this.blockyStorage.EMPTYBLOCKXML || xml === this.blockyStorage.EMPTYBLOCKXML_FOR_GLOBAL;
  }

  public isUniqueIdFromIfThen() {
    if (this.rss.editorItem) return this.rss.editorItems.ifthen.find(ifthen => ifthen.uniqueId === this.rss.editorItem?.uniqueId);
    return undefined;
  }
}
